import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogSummaries } from "@/blog/blogIndex";
import { trackBlogPostOpen } from "@/lib/analytics";

export default function BlogListPage() {
  const navigate = useNavigate();

  const openPost = (post: (typeof blogSummaries)[number]) => {
    void trackBlogPostOpen(post.id, post.title);
    navigate(`/blog/${post.id}`);
  };

  return (
    <div className="min-h-screen bg-stem-cream">
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-16 bg-stem-dark">
          <div className="section-padding max-w-6xl mx-auto">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium mb-4 md:mb-6 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </button>
            <span className="label text-revelBlue mb-3 block">
              Stories & Ideas
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-3">
              From My Notebook
            </h1>
            <p className="text-white/60 text-sm md:text-base max-w-xl">
              Stories about building, learning, failing, and trying again. Every
              project taught me something — here is the full story behind each
              one.
            </p>
          </div>
        </section>

        {/* Posts List */}
        <section className="py-16 md:py-24">
          <div className="section-padding max-w-6xl mx-auto">
            <div className="space-y-12 md:space-y-16">
              {blogSummaries.map((post, idx) => (
                <article key={post.id}>
                  {/* Image */}
                  <div
                    onClick={() => openPost(post)}
                    className="aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden mb-6 md:mb-8 cursor-pointer group"
                  >
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-revelBlue/10 text-revelBlue text-xs font-semibold rounded-full uppercase tracking-wide">
                      {post.category}
                    </span>
                    <span className="text-xs text-stem-gray">
                      {post.readTime}
                    </span>
                    <span className="text-xs text-stem-lightgray">
                      {post.date}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-stem-dark mb-4 leading-snug">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-stem-charcoal text-sm md:text-base leading-relaxed mb-5">
                    {post.excerpt}
                  </p>

                  {/* Content Preview — first 2 paragraphs */}
                  <div className="space-y-3 mb-6">
                    {post.preview.map((paragraph, pIdx) => (
                      <p
                        key={pIdx}
                        className="text-stem-gray text-sm leading-relaxed"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Read More */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openPost(post)}
                      className="btn-dark text-sm py-2.5 px-5 whitespace-nowrap"
                    >
                      Read Full Story
                      <i className="ri-arrow-right-line" />
                    </button>
                    {post.takeawayCount > 0 && (
                      <span className="text-xs text-stem-lightgray">
                        {post.takeawayCount} key takeaways
                      </span>
                    )}
                  </div>

                  {/* Divider between posts */}
                  {idx < blogSummaries.length - 1 && (
                    <div className="mt-12 md:mt-16 border-t border-stem-sand" />
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
